import { useState, useEffect } from "react";
import {
  collection,
  addDoc,
  doc,
  onSnapshot,
  updateDoc,
  arrayUnion,
  arrayRemove,
  deleteDoc,
  getDoc,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase/firestore";
import { auth } from "../firebase/auth";
import { ConfirmDialog } from "../components/ConfirmDialog";

interface Reply {
  id: string;
  text: string;
  authorId: string;
  authorName: string;
  createdAt: Date | null;
}

interface Question {
  id: string;
  text: string;
  authorId: string;
  authorName: string;
  createdAt: Date | null;
  replies?: Reply[];
}

export default function Page4() {
  const currentUser = auth.currentUser;

  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [newQuestion, setNewQuestion] = useState("");
  const [newReply, setNewReply] = useState<{ [questionId: string]: string }>({});

  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null);
  const [editingQuestionText, setEditingQuestionText] = useState("");
  const [editingReplyId, setEditingReplyId] = useState<string | null>(null);
  const [editingReplyText, setEditingReplyText] = useState("");

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{
    type: "question" | "reply";
    id: string;
    parentId?: string;
  } | null>(null);

  // 🛠 Fix placement of this function
  const closeConfirmDialog = () => {
    setItemToDelete(null);
    setConfirmOpen(false);
  };

  const handleDeleteConfirmed = async () => {
    if (!itemToDelete || !currentUser) {
      closeConfirmDialog();
      return;
    }

    try {
      if (itemToDelete.type === "question") {
        await deleteDoc(doc(db, "faq", itemToDelete.id));
      } else if (itemToDelete.type === "reply" && itemToDelete.parentId) {
        const questionRef = doc(db, "faq", itemToDelete.parentId);
        const questionSnap = await getDoc(questionRef);
        if (!questionSnap.exists()) return;
        const data = questionSnap.data();
        const replyToRemove = data.replies.find((r: Reply) => r.id === itemToDelete.id);
        if (!replyToRemove) return;

        await updateDoc(questionRef, {
          replies: arrayRemove(replyToRemove),
        });
      }
    } catch (err) {
      console.error("Error deleting:", err);
    } finally {
      closeConfirmDialog();
    }
  };

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "faq"), (snapshot) => {
      const fetchedQuestions: Question[] = snapshot.docs.map((doc) => {
        const data = doc.data();
        const createdAt =
          data.createdAt instanceof Timestamp ? data.createdAt.toDate() : null;
        const replies: Reply[] = (data.replies || []).map((r: any) => ({
          ...r,
          createdAt: r.createdAt instanceof Timestamp ? r.createdAt.toDate() : null,
        }));
        return {
          id: doc.id,
          text: data.text,
          authorId: data.authorId,
          authorName: data.authorName,
          createdAt,
          replies,
        };
      });

      fetchedQuestions.sort((a, b) => {
        const aDate = a.createdAt || new Date(0);
        const bDate = b.createdAt || new Date(0);
        return bDate.getTime() - aDate.getTime();
      });

      setQuestions(fetchedQuestions);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleAddQuestion = async () => {
    if (!currentUser || !newQuestion.trim()) return;
    try {
      await addDoc(collection(db, "faq"), {
        text: newQuestion.trim(),
        authorId: currentUser.uid,
        authorName: currentUser.displayName || currentUser.phoneNumber || "Anonymous",
        createdAt: serverTimestamp(),
        replies: [],
      });
      setNewQuestion("");
    } catch (err) {
      console.error("Error adding question:", err);
    }
  };

  const handleAddReply = async (questionId: string) => {
    if (!currentUser || !newReply[questionId]?.trim()) return;
    try {
      const questionRef = doc(db, "faq", questionId);
      const reply: Reply = {
        id: crypto.randomUUID(),
        text: newReply[questionId].trim(),
        authorId: currentUser.uid,
        authorName: currentUser.displayName || currentUser.phoneNumber || "Anonymous",
        createdAt: Timestamp.now().toDate(), // Convert immediately
      };

      await updateDoc(questionRef, {
        replies: arrayUnion(reply),
      });
      setNewReply((prev) => ({ ...prev, [questionId]: "" }));
    } catch (err) {
      console.error("Error adding reply:", err);
    }
  };

  const startEditingQuestion = (id: string, text: string) => {
    setEditingQuestionId(id);
    setEditingQuestionText(text);
  };

  const saveEditedQuestion = async () => {
    if (!editingQuestionId || !editingQuestionText.trim()) return;
    try {
      const ref = doc(db, "faq", editingQuestionId);
      await updateDoc(ref, { text: editingQuestionText.trim() });
      setEditingQuestionId(null);
      setEditingQuestionText("");
    } catch (err) {
      console.error("Error updating question:", err);
    }
  };

  const startEditingReply = (replyId: string, text: string) => {
    setEditingReplyId(replyId);
    setEditingReplyText(text);
  };

  const saveEditedReply = async (questionId: string) => {
    if (!editingReplyId || !editingReplyText.trim()) return;
    try {
      const questionRef = doc(db, "faq", questionId);
      const docSnap = await getDoc(questionRef);
      if (!docSnap.exists()) return;
      const data = docSnap.data();
      const updatedReplies = (data?.replies || []).map((r: Reply) =>
        r.id === editingReplyId ? { ...r, text: editingReplyText.trim() } : r
      );

      await updateDoc(questionRef, { replies: updatedReplies });
      setEditingReplyId(null);
      setEditingReplyText("");
    } catch (err) {
      console.error("Error updating reply:", err);
    }
  };

  const openConfirmDialog = (type: "question" | "reply", id: string, parentId?: string) => {
    setItemToDelete({ type, id, parentId });
    setConfirmOpen(true);
  };

  if (loading) return <div className="p-4">Loading FAQ...</div>;

  return (
    <div className="p-4 max-w-3xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold mb-4">FAQ</h1>

      <div className="mb-6">
        <textarea
          value={newQuestion}
          onChange={(e) => setNewQuestion(e.target.value)}
          placeholder="Ask a question..."
          rows={3}
          className="w-full p-2 border rounded resize-none"
        />
        <button
          onClick={handleAddQuestion}
          disabled={!newQuestion.trim()}
          className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          Submit Question
        </button>
      </div>

      {questions.length === 0 && <p>No questions yet. Be the first to ask!</p>}

      {questions.map((q) => (
        <div key={q.id} className="border p-4 rounded space-y-3">
          <div className="flex justify-between items-center">
            <div>
              <span className="font-semibold">{q.authorName}</span> asked:
            </div>
            <div className="text-sm text-gray-500">
              {q.createdAt ? q.createdAt.toLocaleString() : "Just now"}
            </div>
          </div>

          {editingQuestionId === q.id ? (
            <>
              <textarea
                value={editingQuestionText}
                onChange={(e) => setEditingQuestionText(e.target.value)}
                rows={2}
                className="w-full p-2 border rounded resize-none"
              />
              <div className="flex gap-2 mt-1">
                <button
                  onClick={saveEditedQuestion}
                  className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Save
                </button>
                <button
                  onClick={() => setEditingQuestionId(null)}
                  className="px-3 py-1 bg-gray-300 rounded hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <p className="whitespace-pre-wrap">{q.text}</p>
          )}

          {currentUser?.uid === q.authorId && editingQuestionId !== q.id && (
            <div className="flex gap-2">
              <button
                onClick={() => startEditingQuestion(q.id, q.text)}
                className="text-blue-600 hover:underline text-sm"
              >
                Edit
              </button>
              <button
                onClick={() => openConfirmDialog("question", q.id)}
                className="text-red-600 hover:underline text-sm"
              >
                Delete
              </button>
            </div>
          )}

          <div className="pl-4 border-l space-y-3">
            {(q.replies || []).length === 0 && (
              <p className="text-sm text-gray-500">No replies yet.</p>
            )}
            {(q.replies || [])
              .slice()
              .sort((a, b) => {
                const aDate = a.createdAt || new Date(0);
                const bDate = b.createdAt || new Date(0);
                return aDate.getTime() - bDate.getTime();
              })
              .map((r) =>
                editingReplyId === r.id ? (
                  <div key={r.id} className="space-y-1">
                    <textarea
                      value={editingReplyText}
                      onChange={(e) => setEditingReplyText(e.target.value)}
                      rows={2}
                      className="w-full p-2 border rounded resize-none"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => saveEditedReply(q.id)}
                        className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setEditingReplyId(null)}
                        className="px-3 py-1 bg-gray-300 rounded hover:bg-gray-400"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div key={r.id} className="space-y-1">
                    <div className="text-sm text-gray-700 whitespace-pre-wrap">{r.text}</div>
                    <div className="text-xs text-gray-500 flex justify-between">
                      <span>— {r.authorName}</span>
                      <span>{r.createdAt ? r.createdAt.toLocaleString() : "Just now"}</span>
                    </div>
                    {currentUser?.uid === r.authorId && (
                      <div className="flex gap-2 text-xs">
                        <button
                          onClick={() => startEditingReply(r.id, r.text)}
                          className="text-blue-600 hover:underline"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => openConfirmDialog("reply", r.id, q.id)}
                          className="text-red-600 hover:underline"
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                )
              )}

            {/* New reply input */}
            <div className="mt-2">
              <textarea
                value={newReply[q.id] || ""}
                onChange={(e) =>
                  setNewReply((prev) => ({ ...prev, [q.id]: e.target.value }))
                }
                rows={2}
                placeholder="Write a reply..."
                className="w-full p-2 border rounded resize-none"
              />
              <button
                onClick={() => handleAddReply(q.id)}
                disabled={!newReply[q.id]?.trim()}
                className="mt-1 px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                Submit Reply
              </button>
            </div>
          </div>
        </div>
      ))}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmOpen}
        onCancel={closeConfirmDialog}
        onConfirm={handleDeleteConfirmed}
        title={`Delete ${itemToDelete?.type === "question" ? "Question" : "Reply"}`}
        message={`Are you sure you want to delete this ${itemToDelete?.type}? This action cannot be undone.`}
      />
    </div>
  );
}
